import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import { sendAnalyticsEvent } from 'dpl/util/analytics';
import useInfiniteCollectionQuery from 'dpl/shared/fetching/hooks/useInfiniteCollectionQuery';
import { breederProfileBreederTestimonialIndexResourceDefinition } from 'dpl/shared/breeder/utils/queryDefinitions';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { breederTransportOptionsQueryDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { MOBILE_TAB_KEYS } from 'dpl/components/BreederProfile/utils/constants';
import Icon from 'dpl/common/components/Icon';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import SmartImage from 'dpl/components/SmartImage';
import { makeSequence } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import useToggle from 'dpl/shared/hooks/useToggle';
import PuppyTransportOptionsModal from 'dpl/modals/PuppyTransportOptionsModal';
import { getBreederLocationDescription } from 'dpl/components/BreederProfile/utils/helpers';
import useStoriesLabel from 'dpl/common/hooks/useStoriesLabel';

import { useBreederProfileData } from '../../hooks/useBreederProfileData';
import useBreederProfileTabs from '../../../hooks/useBreederProfileTabs';

const IS_LT_MD = isLessThanBreakpoint('md');
const ICON_SIZE = IS_LT_MD ? '20px' : '24px';

// OVERVIEW CARD ITEM
function OverviewCardItemSkeleton() {
  return (
    <li className="flex items-center gap-8">
      <SkeletonBlock className="br-100" width={ICON_SIZE} height={ICON_SIZE} />
      <SkeletonBlock className="font-14 md:font-16" width="80%" height="1em" />
    </li>
  );
}

interface OverviewCardItemProps {
  isLoading: boolean;
  iconName: string;
  description: React.ReactNode;
}

export function OverviewCardItem({
  isLoading,
  iconName,
  description
}: OverviewCardItemProps) {
  if (isLoading) return <OverviewCardItemSkeleton />;

  return (
    <li className="flex items-center gap-8 font-14 md:font-16 lh-body secondary">
      <Icon
        className="tertiary"
        name={iconName}
        width={ICON_SIZE}
        height={ICON_SIZE}
      />
      {description}
    </li>
  );
}

// PERCENT RECOMMEND ITEM
interface PercentRecommendItemProps {
  percentRecommend: number | null;
  testimonialsCount: number;
  // Pluralized "review(s)" / "owner story/stories" for testimonialsCount
  testimonialsNoun: string;
  onReviewsClick: () => void;
}

export function PercentRecommendItem({
  percentRecommend,
  testimonialsCount,
  testimonialsNoun,
  onReviewsClick
}: PercentRecommendItemProps) {
  if (percentRecommend == null) return null;

  return (
    <OverviewCardItem
      isLoading={false}
      iconName="fetch-verified"
      description={
        <span>
          {percentRecommend}% recommend
          {testimonialsCount > 0 && (
            <>
              {' • '}
              <button
                type="button"
                className="underline stone-700"
                onClick={onReviewsClick}
              >
                {testimonialsCount} {testimonialsNoun}
              </button>
            </>
          )}
        </span>
      }
    />
  );
}

// OVERVIEW CARD CONTENT
interface OverviewCardContentSkeletonProps {
  className?: string;
}

export function OverviewCardContentSkeleton({
  className = ''
}: OverviewCardContentSkeletonProps) {
  return (
    <div
      className={classnames(
        'flex flex-column gap-16 ba br4 b--default pv4 ph4 font-14',
        className
      )}
    >
      <div className="flex items-center gap-16">
        <SkeletonBlock className="br2" height="80px" width="80px" />
        <div className="flex-auto flex-basis-0">
          <SkeletonBlock className="font-20 mb1" height="1.2em" width="70%" />
          <SkeletonBlock className="font-14 mb1" height="1.4em" width="80%" />
          <SkeletonBlock className="font-14" height="1.4em" width="50%" />
        </div>
      </div>
      <ul className="flex flex-column gap-4">
        {makeSequence(2).map((index: number) => (
          <OverviewCardItemSkeleton key={index} />
        ))}
      </ul>
    </div>
  );
}

interface OverviewCardContentProps {
  analytics?: {
    view?: string;
    view_feature_location?: string;
  };
  className?: string;
  breeder: {
    avatar_profile_photo_url?: string;
    display_name?: string;
    program_name?: string;
    user_id?: number;
    initials?: string;
  };
  testimonialsCount?: number;
  percentRecommend?: number | null;
  onReviewsClick: () => void;
  overviewItems: {
    isLoading: boolean;
    iconName: string;
    description: React.ReactNode;
  }[];
}

export function OverviewCardContent({
  analytics = {},
  className = '',
  breeder,
  testimonialsCount = 0,
  percentRecommend = null,
  onReviewsClick,
  overviewItems
}: OverviewCardContentProps) {
  const showPercentRecommend = percentRecommend != null;
  const showReviewsButton = !showPercentRecommend && testimonialsCount > 0;
  const showOverviewItems = !isEmpty(overviewItems) || showPercentRecommend;

  const { shortNoun, fullNoun } = useStoriesLabel(testimonialsCount);

  function handleReviewsClick() {
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      button_cta: 'Reviews',
      view_feature: ANALYTICS_VIEW_FEATURES.REVIEWS_HYPERLINK,
      ...analytics
    });

    onReviewsClick();
  }

  return (
    <div
      className={classnames(
        'flex flex-column gap-16 ba br4 b--default pv4 ph4 pv6-md ph6-md lh-body',
        className
      )}
    >
      <div className="flex items-center gap-16">
        {breeder.avatar_profile_photo_url ? (
          <SmartImage
            Tag="div"
            src={breeder.avatar_profile_photo_url}
            className="bg-center cover h-100 w-100 br3"
            width="80px"
            height="80px"
          />
        ) : (
          <UserAvatar
            className="br3"
            shape="square"
            size="xxl"
            // @ts-ignore
            user={{ id: breeder.user_id, initials: breeder.initials }}
          />
        )}
        <div className="flex-auto flex-basis-0">
          <p className="font-20 font-serif fw-medium lh-title truncate">
            Raised by {breeder.display_name}
          </p>
          <p className="font-14 secondary">{breeder.program_name}</p>
          {showReviewsButton && (
            <button
              type="button"
              className="font-14 underline stone-700 db"
              onClick={handleReviewsClick}
            >
              {testimonialsCount} owner {shortNoun}
            </button>
          )}
        </div>
      </div>
      {showOverviewItems && (
        <ul className="flex flex-column gap-4">
          {overviewItems.map(({ isLoading, iconName, description }) => (
            <OverviewCardItem
              key={description as string}
              isLoading={isLoading}
              iconName={iconName}
              description={description}
            />
          ))}
          <PercentRecommendItem
            percentRecommend={percentRecommend}
            testimonialsCount={testimonialsCount}
            testimonialsNoun={fullNoun}
            onReviewsClick={handleReviewsClick}
          />
        </ul>
      )}
    </div>
  );
}

// OVERVIEW CARD
export default function OverviewCard() {
  const testimonialsCount = useSelector(
    ({ server }: any) => server.testimonialsCount
  );
  const { breeder, breederProfileId, locName, isFetchingBreeder } =
    useBreederProfileData();
  const { replaceHash } = useBreederProfileTabs();
  const [isTransportModalOpen, toggleTransportModalOpen] = useToggle(false);

  const { data: testimonialsData } = useInfiniteCollectionQuery(
    // @ts-ignore
    breederProfileBreederTestimonialIndexResourceDefinition(breederProfileId, {
      pageNumber: 1,
      perPage: 3
    })
  );
  const percentRecommend =
    testimonialsData?.pages?.[0]?.data?.percent_recommend ?? null;

  const {
    isFetching: isFetchingLocation,
    value: {
      data: { location, sorted_transport_options: transportOptions }
    }
  } = useResourceQuery(
    breederTransportOptionsQueryDefinition(breederProfileId, { locName })
  );

  const locationDescription = useMemo(() => {
    return getBreederLocationDescription({
      location,
      onDropOffClick: transportOptions?.length
        ? toggleTransportModalOpen
        : undefined
    });
  }, [location, transportOptions, toggleTransportModalOpen]);

  const overviewItems = useMemo(() => {
    return [
      (isFetchingLocation || !isEmpty(locationDescription)) && {
        isLoading: isFetchingLocation,
        iconName: 'fetch-location',
        description: locationDescription
      },
      breeder.meet_up_availability_sentence && {
        isLoading: false,
        iconName: 'fetch-transportation-car',
        description: breeder.meet_up_availability_sentence
      },
      !isEmpty(breeder.response_time_description) && {
        isLoading: false,
        iconName: 'fetch-message',
        description: breeder.response_time_description
      }
    ].filter(Boolean);
  }, [isFetchingLocation, breeder, locationDescription]);

  if (isFetchingBreeder) {
    return (
      <div className="container-xxl mb8">
        <OverviewCardContentSkeleton />
      </div>
    );
  }

  return (
    <div className="OverviewCard container-xxl mb8">
      <OverviewCardContent
        analytics={{
          view: ANALYTICS_VIEWS.BREEDER_PROFILE,
          view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.OVERVIEW_TAB
        }}
        breeder={{
          ...breeder,
          user_id: breeder.user.id,
          program_name: breeder.name
        }}
        testimonialsCount={testimonialsCount}
        percentRecommend={percentRecommend}
        onReviewsClick={() => replaceHash(MOBILE_TAB_KEYS.STORIES)}
        // @ts-ignore
        overviewItems={overviewItems}
      />
      <PuppyTransportOptionsModal
        breeder={{
          avatar_profile_photo_url: breeder.avatar_profile_photo_url,
          first_name: breeder.user.first_name,
          full_name: breeder.user.full_name,
          user_id: breeder.user.id,
          initials: breeder.initials
        }}
        transportOptions={transportOptions || []}
        isOpen={isTransportModalOpen}
        onClose={toggleTransportModalOpen}
      />
    </div>
  );
}
